import { alphaSorter } from 'vet-bones/utils';

import { DataSourceTypeDescription } from 'src/constants/dataSourceTypeDescription';
import {
  FileUpload,
  Mapping,
  RelationshipMapping,
  VirtualGraph,
} from 'src/containers/ProjectDiagram/types/data';
import {
  isFileUpload,
  isVirtualGraph,
} from 'src/containers/ProjectDiagram/utils/data';
import {
  CsvMappingInfo,
  VirtualGraphMappingInfo,
} from 'src/containers/PublishProjectDrawer/types';
import {
  Project,
  ProjectVirtualGraph,
  ProjectVirtualGraphOptions,
} from 'src/store/state/projects';
import { exportFileUpload } from 'src/utils/export/fileUpload';
import { getAllProjectNamespaces } from 'src/utils/export/namespaces';
import { ExportFileUpload, ExportVirtualGraph } from 'src/utils/export/types';
import { exportVirtualGraph } from 'src/utils/export/virtualGraph';
import { stringToFilename } from 'src/utils/stringToFilename';

const propertiesToString = (options: ProjectVirtualGraphOptions) => {
  return Object.entries(options)
    .map((pair) => pair.join(':'))
    .sort(alphaSorter)
    .join(',');
};

export const exportResources = ({
  project,
  classMappingsByResource,
  relationshipMappingsByResource,
  typeDescription,
  csvMappingInfo,
  vgMappingInfo,
}: {
  project: Project;
  classMappingsByResource: Record<string, Mapping[]>;
  relationshipMappingsByResource: Record<string, RelationshipMapping[]>;
  typeDescription: DataSourceTypeDescription;
  csvMappingInfo?: Record<string, CsvMappingInfo>;
  vgMappingInfo?: Record<string, VirtualGraphMappingInfo>;
}): {
  fileUploadExports: ExportFileUpload[];
  virtualGraphExports: ExportVirtualGraph[];
} => {
  const { diagram, fileUploadsById, virtualGraphsById } = project;
  const namespaces = getAllProjectNamespaces(project);
  if (!diagram) {
    return {
      fileUploadExports: [],
      virtualGraphExports: [],
    };
  }

  const usedFolderNames: Record<string, number> = {};
  const getUniqueFolderName = (name: string) => {
    if (usedFolderNames[name] === undefined) {
      usedFolderNames[name] = 0;
      return name;
    }

    usedFolderNames[name] += 1;
    return `${name} (${usedFolderNames[name]})`;
  };

  const fileUploadExports = diagram.model.nodeDataArray
    .filter(
      (data): data is FileUpload =>
        isFileUpload(data) &&
        Boolean(fileUploadsById[data.value]) &&
        Boolean(classMappingsByResource[data.value])
    )
    .map<ExportFileUpload>((data) => ({
      id: data.value,
      folderName: getUniqueFolderName(`${stringToFilename(data.text)}`),
      resourceName: data.text,
      mappingInfo: csvMappingInfo?.[data.value],
      files: exportFileUpload({
        data,
        diagram,
        fileUpload: project.fileUploadsById[data.value],
        classMappings: classMappingsByResource[data.value] || [],
        relationshipMappings: relationshipMappingsByResource[data.value] || [],
        dataNamespace: project.dataNamespace,
        namespaces,
        csvOptions: csvMappingInfo?.[data.value]?.fileOptions,
      }),
    }));

  // Combine virtual graphs from the same data source and with the same
  // target name and properties into the same folder / published virtual graph
  const virtualGraphs: Record<
    string, // target virtual graph name
    Record<
      string, // data source + property hash
      {
        dataSourceId: string;
        properties: Record<string, any>;
        mappingInfo?: VirtualGraphMappingInfo;
        virtualGraphsAndMappings: Array<{
          virtualGraph: ProjectVirtualGraph;
          classMappings: Mapping[];
          relationshipMappings: RelationshipMapping[];
        }>;
      }
    >
  > = {};
  diagram.model.nodeDataArray
    .filter(
      (data): data is VirtualGraph =>
        isVirtualGraph(data) &&
        Boolean(virtualGraphsById[data.value]) &&
        Boolean(classMappingsByResource[data.value])
    )
    .forEach((data) => {
      const mappingInfo = vgMappingInfo?.[data.value];
      const virtualGraph = project.virtualGraphsById[data.value];
      const { dataSourceId, options: properties } = virtualGraph;

      const virtualGraphName = mappingInfo?.virtualGraphName || dataSourceId;
      if (!virtualGraphs[virtualGraphName]) {
        virtualGraphs[virtualGraphName] = {};
      }

      const hash = `${dataSourceId}-${propertiesToString(properties)}`;
      if (!virtualGraphs[virtualGraphName][hash]) {
        virtualGraphs[virtualGraphName][hash] = {
          dataSourceId,
          properties,
          mappingInfo,
          virtualGraphsAndMappings: [],
        };
      }
      virtualGraphs[virtualGraphName][hash].virtualGraphsAndMappings.push({
        virtualGraph,
        classMappings: classMappingsByResource[data.value] || [],
        relationshipMappings: relationshipMappingsByResource[data.value] || [],
      });
    });

  const virtualGraphExports: ExportVirtualGraph[] = [];
  Object.entries(virtualGraphs).forEach(([virtualGraphName, exportsByHash]) => {
    const exports = Object.values(exportsByHash);
    exports.forEach(
      (
        { dataSourceId, properties, mappingInfo, virtualGraphsAndMappings },
        diffPropertiesIndex
      ) => {
        let virtualGraphId = virtualGraphName;
        if (exports.length > 1) {
          // Only append a _# number when there are virtual graphs with different properties
          virtualGraphId = `${virtualGraphId}_${diffPropertiesIndex + 1}`;
        }

        virtualGraphExports.push({
          id: virtualGraphId,
          dataSource: dataSourceId,
          folderName: getUniqueFolderName(stringToFilename(virtualGraphId)),
          resourceName: virtualGraphName,
          mappingInfo,
          properties,
          files: exportVirtualGraph({
            id: virtualGraphId,
            diagram,
            dataNamespace: project.dataNamespace,
            namespaces,
            typeDescription,
            virtualGraphsAndMappings,
          }),
        });
      }
    );
  });

  return { fileUploadExports, virtualGraphExports };
};
